import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Star, Lock, ExternalLink, Download, Copy, Loader2, Sparkles,
  CheckCircle2, ArrowRight, PartyPopper, Trophy,
} from "lucide-react";

const TIER_GATE_LABEL = {
  free: "Free", pro: "Pro+", hustler: "Hustler+", team: "Team",
};

export default function ResourceDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeadPicker, setShowLeadPicker] = useState(false);
  const [celebration, setCelebration] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/resources/${slug}`);
      setR(res.data);
      const newlyCompleted = res.data?.newly_completed_tracks?.[0];
      if (newlyCompleted) {
        setCelebration(newlyCompleted);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Resource not found");
        nav("/app/resources", { replace: true });
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load resource");
      }
    } finally {
      setLoading(false);
    }
  }, [slug, nav]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleFavorite = async () => {
    if (!r) return;
    try {
      const res = await api.post(`/resources/${r.id}/favorite`);
      setR((p) => ({ ...p, is_favorited: res.data.favorited }));
      toast.success(res.data.favorited ? "Added to your Library" : "Removed from your Library");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update favorite");
    }
  };

  const copyBody = async () => {
    if (!r?.body_md) return;
    try {
      await navigator.clipboard.writeText(r.body_md);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading || !r) {
    return (
      <div className="card p-12 text-center text-[var(--ink-2)]">
        <Loader2 size={20} className="animate-spin inline" /> Loading…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="resource-detail">
      <Link to="/app/resources" className="text-sm text-[var(--ink-2)] hover:text-[var(--primary)] inline-flex items-center gap-1 w-fit" data-testid="resource-back">
        <ArrowLeft size={14} /> Back to Resources
      </Link>

      <div className="card p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-700">
                {r.category}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-800">
                {TIER_GATE_LABEL[r.tier_gate] || "Free"}
              </span>
              {r.featured && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold inline-flex items-center gap-1 bg-amber-50 text-amber-800">
                  <Sparkles size={10} /> Featured
                </span>
              )}
            </div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight">{r.title}</h1>
            {r.summary && (
              <p className="text-[var(--ink-2)] text-base mt-2 leading-relaxed">{r.summary}</p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              className={"btn " + (r.is_favorited ? "btn-primary" : "btn-ghost")}
              onClick={toggleFavorite}
              data-testid="resource-favorite-btn"
            >
              <Star size={14} className={r.is_favorited ? "fill-current" : ""} />
              {r.is_favorited ? "Saved" : "Save"}
            </button>
          </div>
        </div>

        {r.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {r.tags.map((t) => (
              <span key={t} className="px-2 py-0.5 rounded text-[10px] bg-slate-50 text-slate-600">#{t}</span>
            ))}
          </div>
        )}
      </div>

      {/* Locked CTA */}
      {r.is_locked && (
        <div className="card p-8 text-center bg-gradient-to-br from-amber-50 to-rose-50 border border-amber-200" data-testid="resource-locked-cta">
          <Lock size={32} className="inline mb-3 text-amber-600" />
          <h2 className="font-display font-extrabold text-2xl mb-2">
            Unlock {TIER_GATE_LABEL[r.tier_gate]} Resources
          </h2>
          <p className="text-sm text-[var(--ink-2)] max-w-md mx-auto mb-4">
            This resource is part of our {TIER_GATE_LABEL[r.tier_gate]} library. Upgrade your plan
            to unlock {r.category}s like this one, plus full vendor directories, advanced scripts, and more.
          </p>
          <Link to="/app/billing" className="btn btn-primary inline-flex items-center gap-1" data-testid="resource-upgrade-btn">
            View Plans <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {/* Video embed */}
      {!r.is_locked && r.category === "video" && r.url && (
        <div className="card p-2 overflow-hidden" data-testid="resource-video">
          <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
            <iframe
              src={r.url}
              title={r.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Download/external link */}
      {!r.is_locked && (r.file_url || (r.url && r.category !== "video")) && (
        <div className="card p-4 flex items-center gap-3 flex-wrap" data-testid="resource-download">
          {r.file_url && (
            <a href={r.file_url} download target="_blank" rel="noreferrer" className="btn btn-primary">
              <Download size={14} /> Download file
            </a>
          )}
          {r.url && r.category !== "video" && (
            r.url.startsWith("/") ? (
              <Link to={r.url} className="btn btn-primary" data-testid="resource-open-in-app">
                <ArrowRight size={14} /> Open in app
              </Link>
            ) : (
              <a href={r.url} target="_blank" rel="noreferrer" className="btn btn-ghost">
                <ExternalLink size={14} /> Open external link
              </a>
            )
          )}
        </div>
      )}

      {/* Body */}
      {!r.is_locked && r.body_md && (
        <div className="card p-6" data-testid="resource-body">
          <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
            {r.category === "script" && (
              <button
                type="button"
                onClick={() => setShowLeadPicker(true)}
                className="btn btn-primary"
                data-testid="resource-use-in-deal-btn"
              >
                <CheckCircle2 size={14} /> Use in a deal
              </button>
            )}
            <button type="button" onClick={copyBody} className="btn btn-ghost" data-testid="resource-copy-btn">
              <Copy size={14} /> Copy
            </button>
          </div>
          <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:font-extrabold prose-h2:text-2xl prose-h3:text-lg prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-code:text-rose-600 prose-table:text-sm prose-th:bg-slate-50">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {r.body_md}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {showLeadPicker && (
        <LeadPickerModal
          onClose={() => setShowLeadPicker(false)}
          bodyMd={r.body_md}
          resourceTitle={r.title}
        />
      )}

      {celebration && (
        <TrackCompletionModal
          track={celebration}
          onClose={() => setCelebration(null)}
        />
      )}
    </div>
  );
}

function TrackCompletionModal({ track, onClose }) {
  const nav = useNavigate();
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-in fade-in" data-testid="track-completion-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md text-center p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-amber-50 opacity-60 pointer-events-none" />
        <div className="relative">
          <div className="text-7xl mb-2 select-none" aria-hidden="true">{track.emoji}</div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1 flex items-center justify-center gap-1.5">
            <PartyPopper size={11} /> Achievement Unlocked
          </div>
          <h2 className="font-display font-extrabold text-2xl leading-tight mb-2" data-testid="track-completion-title">
            You crushed {track.label}!
          </h2>
          <p className="text-sm text-[var(--ink-2)] mb-5 leading-relaxed">
            You finished all <strong>{track.items_count}</strong> resources in this track.
            We made you a certificate to mark it.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="btn btn-primary w-full justify-center"
              onClick={() => { onClose(); nav(`/app/resources/certificate/${track.id}`); }}
              data-testid="track-completion-view-cert"
            >
              <Trophy size={14} /> View &amp; share certificate
            </button>
            <button
              type="button"
              className="text-xs text-[var(--ink-3)] hover:text-[var(--ink-2)] py-1"
              onClick={onClose}
              data-testid="track-completion-dismiss"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Lead picker — append script to a lead's notes ──────────────────────────

function LeadPickerModal({ onClose, bodyMd, resourceTitle }) {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/leads");
        setLeads(r.data || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load leads");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = leads.filter((l) => {
    const hay = `${l.address || ""} ${l.seller_name || ""} ${l.city || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const attach = async (lead) => {
    setSaving(lead.id);
    try {
      const stamp = new Date().toLocaleString();
      const newNotes = (lead.notes ? lead.notes + "\n\n" : "")
        + `--- ${resourceTitle} (added ${stamp}) ---\n${bodyMd}`;
      await api.patch(`/leads/${lead.id}`, { notes: newNotes });
      toast.success(`Added to ${lead.address || lead.seller_name || "lead"}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to attach");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="lead-picker-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="p-4 border-b">
          <div className="font-display font-extrabold text-lg">Attach to a lead</div>
          <div className="text-xs text-[var(--ink-2)] mt-1">
            This script will be appended to the lead&apos;s notes.
          </div>
        </div>
        <div className="p-4 border-b">
          <input
            type="text"
            placeholder="Search leads by address, name, city…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
            data-testid="lead-picker-search"
            autoFocus
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="text-center text-sm text-[var(--ink-2)] p-8">
              <Loader2 size={16} className="animate-spin inline" /> Loading leads…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-sm text-[var(--ink-3)] p-8">No leads match.</div>
          ) : (
            filtered.slice(0, 50).map((l) => (
              <button
                type="button"
                key={l.id}
                onClick={() => attach(l)}
                disabled={saving === l.id}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 disabled:opacity-50 flex items-center justify-between gap-2"
                data-testid="lead-picker-row"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{l.address || "—"}</div>
                  <div className="text-xs text-[var(--ink-2)] truncate">
                    {l.seller_name || "—"} · {l.city || "—"} · {l.stage}
                  </div>
                </div>
                {saving === l.id
                  ? <Loader2 size={14} className="animate-spin" />
                  : <ArrowRight size={14} className="text-[var(--ink-3)]" />}
              </button>
            ))
          )}
        </div>
        <div className="p-3 border-t flex justify-end">
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="lead-picker-close">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
